import React from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";

import NumberField from "src/components/NumberField";
import LoadableButton from "src/components/LoadableButton";
import { updateReconciliation } from "src/apiService/modules/reconciliations";
import ConfirmDialog from "src/components/ConfirmDialog";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import {
  calendarYmdInTimeZone,
  startOfCalendarDayContainingInstant,
  tryYmdStringToStartOfDayUtc,
} from "src/utils/timezone";
import type { BankAccountWithReconciliations } from "./types";

const useStyles = makeStyles({
  content: {
    "&:first-child": {
      paddingTop: 0,
    },
  },
});

interface ContentProps {
  bankAccount: BankAccountWithReconciliations;
  onClose: () => void;
  onReconciliationUpdated: (reconciliation: Reconciliation) => void;
}

function Content({
  bankAccount,
  onClose,
  onReconciliationUpdated,
}: ContentProps) {
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const account = bankAccount.name;
  const initialBankReconciliation = bankAccount.reconciliations[0];
  const initialReconciliationDate =
    tryYmdStringToStartOfDayUtc(initialBankReconciliation.date, timeZone) ??
    null;
  const classes = useStyles();

  const formik = useFormik({
    initialValues: {
      confirm: false,
      date: initialReconciliationDate,
      balance: initialBankReconciliation.balance,
    } as { date: Date | null; balance: number | null; confirm: boolean },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.balance && values.balance !== 0)
        errors.balance = "Required field";
      if (!values.date) errors.date = "Required field";
      return errors;
    },
    onSubmit: async (values) => {
      if (
        values.balance === null ||
        values.balance === undefined ||
        !values.date ||
        !initialBankReconciliation ||
        !initialReconciliationDate
      )
        return;
      const initialDateYmd = calendarYmdInTimeZone(
        initialReconciliationDate,
        timeZone
      );
      const nextDateYmd = calendarYmdInTimeZone(values.date, timeZone);
      const unchanged =
        values.balance === initialBankReconciliation.balance &&
        initialDateYmd === nextDateYmd;
      if (unchanged) {
        onClose();
        return;
      }
      if (bankAccount.reconciliations.length > 1) {
        formik.setValues((v) => ({ ...v, confirm: true }));
      } else {
        const nextDateYmd = calendarYmdInTimeZone(values.date, timeZone);
        const response = await updateReconciliation(
          initialBankReconciliation.id,
          {
            balance: values.balance,
            date: nextDateYmd,
          }
        );
        onReconciliationUpdated({
          ...initialBankReconciliation,
          id: response.id,
          open: Boolean(response.open),
          balance: response.balance,
          bank_account_id: response.bank_account_id,
          transactions: response.transactions,
        });
        if (Array.isArray(response.affectedReconciliations)) {
          for (const affected of response.affectedReconciliations) {
            onReconciliationUpdated({
              ...initialBankReconciliation,
              id: affected.id,
              open: affected.open,
              balance: affected.balance,
              bank_account_id: affected.bank_account_id,
            });
          }
        }
        onClose();
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent className={classes.content}>
          <TextField
            value={account}
            inputProps={{ readOnly: true }}
            margin="dense"
            variant="outlined"
            fullWidth
            label="Account"
          />
          <NumberField
            allowNegative
            autoComplete="off"
            margin="dense"
            fullWidth
            required
            label="Beginning Balance"
            value={formik.values.balance ?? ""}
            variant="outlined"
            onChange={formik.handleChange}
            name="balance"
            helperText={formik.errors?.balance || undefined}
            error={!!formik.errors?.balance}
            disabled={formik.isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
          <Box pt={1}>
            <ZonedKeyboardDatePicker
              fullWidth
              variant="inline"
              inputVariant="outlined"
              disableToolbar
              format="MM/dd/yyyy"
              label="Beginning Date"
              required
              autoOk
              onChange={(d) => {
                if (d) {
                  const date = startOfCalendarDayContainingInstant(d, timeZone);
                  formik.setValues((v) => ({
                    ...v,
                    date,
                  }));
                } else {
                  formik.setValues((v) => ({
                    ...v,
                    date: null,
                  }));
                }
              }}
              value={formik.values.date || null}
              helperText={formik.errors?.date || undefined}
              error={!!formik.errors?.date}
              disabled={
                bankAccount.reconciliations.length > 1 || formik.isSubmitting
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <LoadableButton
            color="primary"
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
          >
            Update
          </LoadableButton>
        </DialogActions>
      </form>
      <ConfirmDialog
        open={formik.values.confirm || false}
        title="Are you sure?"
        text={`You are about to change reconciliation information for the ${account} account. Doing this will impact future reconciliations for ${account}. Are you sure you'd like to make this update?`}
        onConfirm={async () => {
          if (bankAccount.reconciliations.length <= 1) return;
          if (
            formik.values.balance === null ||
            formik.values.balance === undefined ||
            !formik.values.date ||
            !initialBankReconciliation
          )
            return;
          const nextDateYmd = calendarYmdInTimeZone(
            formik.values.date,
            timeZone
          );

          const response = await updateReconciliation(
            initialBankReconciliation.id,
            {
              balance: formik.values.balance,
              date: nextDateYmd,
            }
          );
          onReconciliationUpdated({
            ...initialBankReconciliation,
            id: response.id,
            open: Boolean(response.open),
            balance: response.balance,
            bank_account_id: response.bank_account_id,
            transactions: response.transactions,
          });
          if (Array.isArray(response.affectedReconciliations)) {
            for (const affected of response.affectedReconciliations) {
              onReconciliationUpdated({
                ...initialBankReconciliation,
                id: affected.id,
                open: affected.open,
                balance: affected.balance,
                bank_account_id: affected.bank_account_id,
              });
            }
          }
          onClose();
        }}
        onCancel={() => {
          formik.setValues((v) => ({ ...v, confirm: false }));
        }}
      />
    </>
  );
}

function EditBeginningBalanceDialog({
  open,
  onClose,
  bankAccount,
  onReconciliationUpdated,
}: ContentProps & { open: boolean }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Reconciliation</DialogTitle>
      <Content
        bankAccount={bankAccount}
        onClose={onClose}
        onReconciliationUpdated={onReconciliationUpdated}
      />
    </Dialog>
  );
}

export default EditBeginningBalanceDialog;
